/**
 * capacitySplitModel.ts
 * Pure math behind the "Staff this job" v2 SPLIT AVAILABILITY BAR (redesign
 * 2026-07-14, qa/staff-this-job-redesign-2026-07-14/SPEC.md §2). Each
 * (person, week) cell splits a 0–100%+ week into four segments:
 *   - COMMITTED   — firm, live-schedule bookings (green).
 *   - UNCOMMITTED — tentative load from OTHER draft plans (amber hatch).
 *   - THIS PROJECT — the slice being edited in this scenario (blue).
 *   - FREE         — 100 − (the three above), floored at 0.
 * Over-100% is allowed (advisory) — `over` reports the excess.
 *
 * Two testable concerns, no React / MUI / theme:
 *   1. buildCapacitySplit — the RAW numbers (never clipped) that drive the
 *      "C +T +mine" caption + the "N free" / "+N over" badge.
 *   2. splitBarWidths — the VISUAL segment widths for the bar itself, clipped
 *      to a 100%-wide budget in priority order (committed → uncommitted →
 *      this-project → free), respecting the "Show" toggle chips. Mirrors the
 *      approved mockup's `widths()` (capacity-finalized.html).
 * A third concern — the uniform ± stepper's fixed-step / first-add-default
 * logic (SPEC.md: step 20%, press + on 0% jumps to 100%).
 */

/** Which segments the "Show" toolbar chips currently render. */
export interface SplitVisibility {
  committed: boolean;
  uncommitted: boolean;
  free: boolean;
}

/** Default — every segment shown. */
export const DEFAULT_SPLIT_VISIBILITY: SplitVisibility = {
  committed: true,
  uncommitted: true,
  free: true,
};

/** The raw (never visually clipped) split of one (person, week) cell. */
export interface CapacitySplit {
  committed: number;
  uncommitted: number;
  mine: number;
  /** committed + uncommitted + mine (may exceed 100 — over-100 is advisory). */
  total: number;
  /** max(0, 100 − total). */
  free: number;
  /** max(0, total − 100). */
  over: number;
}

/** Round a percent to a non-negative integer (defensive against stale/odd input). */
function nn(v: number | null | undefined): number {
  const n = Math.round(v || 0);
  return n < 0 ? 0 : n;
}

/**
 * Build the raw split for one cell. Never clips — this is the TRUTH the
 * caption + free/over badge read, independent of which segments are toggled
 * visible in the bar.
 */
export function buildCapacitySplit(
  committedPercent: number,
  uncommittedPercent: number,
  minePercent: number,
): CapacitySplit {
  const committed = nn(committedPercent);
  const uncommitted = nn(uncommittedPercent);
  const mine = nn(minePercent);
  const total = committed + uncommitted + mine;
  return {
    committed,
    uncommitted,
    mine,
    total,
    free: Math.max(0, 100 - total),
    over: Math.max(0, total - 100),
  };
}

/** The visual (clipped) segment widths for the bar, summing to ≤ 100. */
export interface SplitBarWidths {
  committed: number;
  uncommitted: number;
  mine: number;
  free: number;
}

/**
 * Allocate the bar's 100%-wide budget in priority order: committed first,
 * then uncommitted, then this-project, then free — each clipped to whatever
 * budget remains. A hidden segment (Show toggle off) is skipped WITHOUT
 * consuming budget, so hiding e.g. "Uncommitted" gives the remaining segments
 * (and the free remainder) more visual room. Mirrors the approved mockup's
 * `widths()` (capacity-finalized.html) so the two never drift.
 */
export function splitBarWidths(
  split: CapacitySplit,
  visibility: SplitVisibility = DEFAULT_SPLIT_VISIBILITY,
): SplitBarWidths {
  let budget = 100;
  const take = (v: number): number => {
    const x = Math.max(0, Math.min(v, budget));
    budget -= x;
    return x;
  };
  const committed = visibility.committed ? take(split.committed) : 0;
  const uncommitted = visibility.uncommitted ? take(split.uncommitted) : 0;
  // This-project is always shown (it's the one being edited).
  const mine = take(split.mine);
  const free = visibility.free ? budget : 0;
  return { committed, uncommitted, mine, free };
}

// ── The uniform ± stepper (SPEC.md: fixed step 20%, first-add jumps to 100%) ──

/** Fixed step for the this-project % stepper (no 5/25 toggle — SPEC.md). */
export const CAPACITY_STEP = 20;

/** Pressing + on a 0% cell jumps straight to a full week (SPEC.md "don't make users set a % every week"). */
export const CAPACITY_FIRST_ADD_DEFAULT = 100;

/**
 * A single project can take at most a whole person-week — the editable
 * this-project share caps at 100% (2026-07-14 feedback). Over-100 only happens
 * on a person's TOTAL when committed/other-draft work stacks on top; the slice
 * itself never exceeds a full week.
 */
export const CAPACITY_MAX = 100;

/** Next value when the − button is pressed (floors at 0). */
export function nextStepDown(current: number, step: number = CAPACITY_STEP): number {
  return Math.max(0, nn(current) - step);
}

/**
 * Next value when the + button is pressed: a 0% cell jumps to the full-week
 * default; any other value steps up by the fixed step — capped at 100%.
 */
export function nextStepUp(
  current: number,
  step: number = CAPACITY_STEP,
  firstAddDefault: number = CAPACITY_FIRST_ADD_DEFAULT,
): number {
  const cur = nn(current);
  const next = cur <= 0 ? firstAddDefault : cur + step;
  return Math.min(CAPACITY_MAX, next);
}

/** Clamp a typed value to 0–100% (a project can't take more than a full week). */
export function clampTypedPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(CAPACITY_MAX, Math.round(value)));
}

/**
 * Free/over badge color intent — 'over' (red) once total > 100 OR the week is
 * exactly full with no room ("fully booked"); 'healthy' (green) once ≥ 50%
 * free; 'tight' (amber) with SOME room left. Mirrors the approved mockup's
 * `freeColor()`. Components map these intents to theme.schedule.* tokens.
 */
export type CapacityFreeColorIntent = 'over' | 'healthy' | 'tight';

export function capacityFreeColorIntent(split: CapacitySplit): CapacityFreeColorIntent {
  if (split.over > 0) return 'over';
  if (split.free >= 50) return 'healthy';
  if (split.free > 0) return 'tight';
  return 'over'; // exactly 100% — fully booked, no room (reads like "over" per the mockup)
}
